import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useApiClient from '../hooks/useApiClient';

const getUnitMeasurement = (um) => {
    const unitMap = {
        PIECE: 'Buc',
        HOUR: 'Oră',
        DAY: 'Zi',
        METER: 'Metru',
        KILOGRAM: 'Kg',
        LITER: 'Litru',
        SET: 'Set',
    };
    return unitMap[um] || um;
};


const PrintableEstimate = () => {
    const [searchParams] = useSearchParams();
    const documentId = searchParams.get('id');
    const [estimateData, setEstimateData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const apiClient = useApiClient();

    useEffect(() => {
        const loadEstimateDocumentData = async () => {
            if (!documentId) {
                setError('ID-ul documentului lipsește.');
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            setError(null);
            try {
                const response = await apiClient.get(`/estimate/one/${documentId}`);
                setEstimateData(response);
            } catch (err) {
                console.error('Eroare la preluarea datelor devizului:', err);
                setError(err.response?.message || err.message || 'Nu s-au putut încărca datele devizului.');
            } finally {
                setIsLoading(false);
            }
        };

        loadEstimateDocumentData();
    }, [documentId]);

    const renderItemsTable = (title, items, showTotal = false) => {
        if (!items || items.length === 0) return null;

        const tableTotal = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
        const vatTotal = items.reduce((sum, item) => sum + (item.vatLine || 0), 0);
        const grandTotal = tableTotal + vatTotal;

        return (
            <div className="mb-4">
                <h3 className="section-title">{title}</h3>
                <table className="items-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Denumire Produs/Serviciu</th>
                            <th className="text-right">UM</th>
                            <th className="text-right">Cant.</th>
                            <th className="text-right">Preț Unitar (fără TVA)</th>
                            <th className="text-right">Valoare (fără TVA)</th>
                            <th className="text-right">Cotă TVA</th>
                            <th className="text-right">Valoare TVA</th>
                            <th className="text-right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, idx) => (
                            <tr key={item.itemId || idx}>
                                <td>{idx + 1}</td>
                                <td>{item.itemName}</td>
                                <td className="text-right">{getUnitMeasurement(item.um)}</td>
                                <td className="text-right">{item.quantity}</td>
                                <td className="text-right">{item.unitPrice.toFixed(2)}</td>
                                <td className="text-right">{item.totalPrice.toFixed(2)}</td>
                                <td className="text-right">{item.vatRate.toFixed(2)}%</td>
                                <td className="text-right">{item.vatLine.toFixed(2)}</td>
                                <td className="text-right">{(item.totalPrice + item.vatLine).toFixed(2)}</td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan="9" className="text-center py-4">Nu sunt {title.toLowerCase()} disponibile.</td></tr>
                        )}
                        {showTotal && (
                            <tr className="total-row">
                                <td colSpan="5" className="text-right">TOTAL {title.toUpperCase()}</td>
                                <td className="text-right">{tableTotal.toFixed(2)}</td>
                                <td></td>
                                <td className="text-right">{vatTotal.toFixed(2)}</td>
                                <td className="text-right">{grandTotal.toFixed(2)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const totalEstimate = useMemo(() => {
        if (!estimateData) return 0;
        const sumTable = (items) => {
            if (!items || items.length === 0) return 0;
            const tableTotal = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
            const vatTotal = items.reduce((sum, item) => sum + (item.vatLine || 0), 0);
            return tableTotal + vatTotal;
        }
        let total = 0;
        total += sumTable(estimateData.labourList);
        total += sumTable(estimateData.productList);
        total += sumTable(estimateData.clientItems);
        return total;
    }, [estimateData]);

    if (isLoading) {
        return <div className="p-8 text-center">Se încarcă documentul...</div>;
    }

    if (error) {
        return (
            <div className="p-8 text-center">
                <h1 className="text-xl font-bold text-red-600 mb-4">Eroare la încărcarea Devizului</h1>
                <p className="text-gray-700">{error}</p>
            </div>
        );
    }

    if (!estimateData) {
        return <div className="p-8 text-center">Nu sunt date disponibile pentru acest document.</div>;
    }

    const { shopBusiness, customerBusiness, carDetails, seriesNumber, date, km, clientFault, shopFault, labourList, productList, clientItems } = estimateData;

    return (
        <div className="printable-container text-gray-800 font-sans text-xs">
            <style>
                {`
                    body {
                        background-color: #f0f0f0;
                    }
                    .printable-container {
                        width: 210mm;
                        margin: 20px auto;
                        padding: 15mm;
                        box-shadow: 0 0 10px rgba(0,0,0,0.1);
                        border: 1px solid #ccc;
                        background: white;
                        max-height: 90vh;
                        overflow-y: auto;
                        overflow-x: hidden;
                    }
                    .document-content {
                        width: 100%;
                    }
                    .page-footer {
                        width: 100%;
                        background: #f8f9fa;
                        border-top: 2px solid #2c3e50;
                        border-bottom: 2px solid #2c3e50;
                        padding: 10px 15px;
                        box-sizing: border-box;
                        margin-top: 30px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    @media print {
                        * {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                            color-adjust: exact !important;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            background-color: #fff !important;
                            width: 100% !important;
                            height: auto !important;
                            overflow: visible !important;
                            position: static !important;
                        }
                        a[href]:after {
                            content: none !important;
                        }
                        .printable-container {
                            width: 100%;
                            margin: 0;
                            padding: 10mm;
                            box-shadow: none;
                            border: none;
                            max-height: none;
                            overflow: visible;
                        }
                        .no-print {
                            display: none !important;
                        }
                        .page-footer {
                            padding: 10px;
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                        table {
                            page-break-inside: auto;
                        }
                        tbody tr {
                            page-break-inside: avoid;
                            break-inside: avoid;
                        }
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    th, td {
                        padding: 4px 6px;
                        text-align: left;
                    }
                    .header-main {
                        background-color: #2c3e50;
                        color: white;
                        padding: 15px;
                        margin-bottom: 20px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .header-main h1 {
                        font-size: 1.8em;
                        font-weight: bold;
                        margin:0;
                    }
                    .header-main p {
                        font-size: 0.9em;
                        margin:0;
                    }
                    .details-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 20px;
                        margin-bottom: 20px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .details-box {
                        border: 1px solid #333;
                        padding: 12px;
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .details-box h3 {
                        font-weight: bold;
                        margin-bottom: 5px;
                        font-size: 0.9em;
                        text-transform: uppercase;
                    }
                    .items-table th {
                        border-bottom: 1px solid #555;
                        font-weight: bold;
                    }
                    .items-table td {
                        border-bottom: 1px dotted #ccc;
                    }
                    .items-table .text-right { text-align: right; }
                    .total-row td {
                        font-weight: bold;
                        border-top: 2px solid #333;
                        padding-top: 8px;
                    }
                    .items-table tbody tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                    .section-title {
                        font-weight: bold;
                        text-transform: uppercase;
                        margin-top: 8px;
                        margin-bottom: 4px;
                        font-size: 0.9em;
                    }
                    .mb-4 {
                        margin-bottom: 16px;
                    }
                `}
            </style>

            <div className="no-print mb-6 flex gap-3">
                <button
                    onClick={() => window.print()}
                    className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                    Printează / Salvează PDF
                </button>
            </div>

            <div className="document-content">
                <div className="header-main">
                    <h1>DEVIZ DE LUCRĂRI</h1>
                    <p className='py-2'>Data: {new Date(date).toLocaleDateString('ro-RO')}</p>
                    {seriesNumber && <p>Serie și număr: {seriesNumber}</p>}
                </div>

                <div className="details-grid">
                    <div className="details-box">
                        <h3>PRESTATOR</h3>
                        <p><strong>{shopBusiness.name}</strong></p>
                        {shopBusiness.tax_identifier && <p>CUI/CNP: {shopBusiness.tax_identifier}</p>}
                        <p>Adresa: {shopBusiness.address || '-'}</p>
                        {shopBusiness.phone && <p>Tel: {shopBusiness.phone}</p>}
                        {shopBusiness.email && <p>Email: {shopBusiness.email}</p>}
                    </div>
                    <div className="details-box">
                        <h3>CLIENT</h3>
                        <p><strong>{customerBusiness.name}</strong></p>
                        {customerBusiness.tax_identifier && <p>CUI/CNP: {customerBusiness.tax_identifier}</p>}
                        <p>Adresa: {customerBusiness.address || '-'}</p>
                        {customerBusiness.phone && <p>Tel: {customerBusiness.phone}</p>}
                        {customerBusiness.email && <p>Email: {customerBusiness.email}</p>}
                    </div>
                </div>

                {carDetails && (
                    <div className="details-box mb-4">
                        <h3 className="section-title">DETALII VEHICUL</h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <p><strong>Marca:</strong> {carDetails.make || '-'}</p>
                            <p><strong>Model:</strong> {carDetails.type || '-'}</p>
                            <p><strong>Număr înmatriculare:</strong> {carDetails.registrationNr || '-'}</p>
                            <p><strong>An:</strong> {carDetails.year || '-'}</p>
                            <p><strong>Capacitate cilindrică:</strong> {carDetails.engineCapacity ? `${carDetails.engineCapacity} cm³` : '-'}</p>
                            <p><strong>Km:</strong> {km || '-'}</p>
                            <p><strong>VIN:</strong> {carDetails.vin || '-'}</p>
                            <p><strong>Cod motor:</strong> {carDetails.engineNumber || '-'}</p>
                            <p><strong>Caroserie:</strong> {carDetails.bodyType || '-'}</p>
                        </div>
                    </div>
                )}

                <div className="details-box mb-4">
                    <h3 className="section-title">Defecte reclamate de client</h3>
                    <p>{clientFault || "N/A"}</p>
                </div>

                <div className="details-box mb-4">
                    <h3 className="section-title">Constatări service</h3>
                    <p>{shopFault || "N/A"}</p>
                </div>

                <div style={{ marginTop: '30px' }}>
                    {renderItemsTable("Manoperă", labourList, false)}
                    {renderItemsTable("Piese", productList, false)}
                    {renderItemsTable("Bunuri Client", clientItems, true)}
                </div>

                {/* Total General Section */}
                <div style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #2c3e50',
                    padding: '15px',
                    marginTop: '20px',
                    marginBottom: '20px',
                    textAlign: 'right',
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid'
                }}>
                    TOTAL GENERAL DEVIZ: {totalEstimate.toFixed(2)} RON
                </div>

                <div style={{
                    border: '1px solid #333',
                    padding: '12px',
                    marginTop: '20px',
                    marginBottom: '20px',
                    fontSize: '0.75em',
                    lineHeight: '1.4',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid'
                }}>
                    <p style={{ margin: 0 }}>
                        <strong>CERTIFICAT DE GARANTIE:</strong> se acorda in conformitate cu OUG 140/2021, OG 21/1992. Pentru piesele achizitionate de la noi, se acorda 12 luni garantie de la data vanzarii. Pentru lucrari cu piesele noi ale clientului sau piesele achizitionate de la noi, garantia pentru lucrarile executate este de 3 luni. <strong>OBS:</strong> In cazul in care clientul aduce piesele pentru montare, acesta este obligat sa prezinte factura de provenienta si certificatul de conformitate respectiv pentru garantie aferent pieselor. In lipsa lor NU SE MONTEAZA PIESELE ADUSE DE CLIENT. <strong>NOTA:</strong> Unitatea noastra nu este raspunzatoare pentru obiectele personale lasate in autovehicul. Am preluat vehiculul cu lucrarile executate conform foii de comanda si cu inventarul complet.
                    </p>
                </div>

                <div className="page-footer">
                    <div className="flex justify-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Client</h3>
                            <p style={{ margin: 0, color: 'rgb(98,98,98)' }}>Semnătură: _________________________</p>
                        </div>
                        <div>
                            <h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Reprezentant Service</h3>
                            <p style={{ margin: 0, color: 'rgb(98,98,98)' }}>Semnătură și ștampilă: _________________________</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintableEstimate;
